import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { DialogHeader } from './DialogHeader';
import { sageCustomerLinkingService } from '../../services/sageCustomerLinkingService';
import { SageCustomerDetails } from '../../types/sage';
import { getFriendlyErrorMessage } from '../../utils/errorUtils';

interface SageLinkCustomerDialogProps {
  open: boolean;
  entityType: string;
  entityId: number;
  /** Pre-seeds the search box and auto-runs a search when the dialog opens. */
  initialSearch?: string;
  /** Optional candidate to pre-select (e.g. the customer named in a 409 conflict). */
  initialCandidate?: SageCustomerDetails | null;
  onClose: () => void;
  /** Fired after a successful link. The parent refreshes its own state; the success snackbar is fired HERE. */
  onLinked: () => void;
}

/**
 * Searchable picker for linking an existing Sage customer to a DRIS entity.
 * Used for Farmers and Agents (which are Sage Customers, not Suppliers).
 *
 * The picker owns all searching (debounced 300ms) — the parent no longer pre-fetches,
 * so there is no double-fetch. A single "Customer linked successfully" snackbar fires
 * here on success; callers must NOT fire a second one.
 */
export const SageLinkCustomerDialog: React.FC<SageLinkCustomerDialogProps> = ({
  open,
  entityType,
  entityId,
  initialSearch,
  initialCandidate,
  onClose,
  onLinked,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [term, setTerm] = useState('');
  const [candidates, setCandidates] = useState<SageCustomerDetails[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [linking, setLinking] = useState(false);
  const [syncDataToDris, setSyncDataToDris] = useState(true);

  // Seed on open / reset on close.
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets dialog state on close; keyed on `open`, no render-time equivalent
      setTerm('');
      setCandidates([]);
      setSelectedId(null);
      setSearching(false);
      setSearchError(false);
      setLinking(false);
      setSyncDataToDris(true);
      return;
    }
    setTerm(initialSearch ?? '');
    setCandidates(initialCandidate ? [initialCandidate] : []);
    setSelectedId(initialCandidate?.id ?? null);
    setSearchError(false);
  }, [open, initialSearch, initialCandidate]);

  // Debounced search (300ms house rule); runs on open (seeded term) and on each keystroke.
  useEffect(() => {
    if (!open) return;
    const q = term.trim();
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchError(false);
      sageCustomerLinkingService
        .searchCustomers(q)
        .then((results) => {
          if (cancelled) return;
          // Keep a pre-seeded candidate visible even if the search didn't return it.
          const seeded =
            initialCandidate && !results.some((r) => r.id === initialCandidate.id)
              ? [initialCandidate]
              : [];
          const list = [...seeded, ...results];
          setCandidates(list);
          setSelectedId((prev) =>
            prev != null && list.some((c) => c.id === prev) ? prev : null,
          );
        })
        .catch(() => {
          if (cancelled) return;
          setSearchError(true);
          setCandidates(initialCandidate ? [initialCandidate] : []);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, term, initialCandidate]);

  const handleLink = useCallback(async () => {
    if (selectedId == null) return;
    setLinking(true);
    try {
      await sageCustomerLinkingService.linkExistingCustomer(entityType, entityId, {
        sageCustomerId: selectedId,
        syncDataToDris,
      });
      enqueueSnackbar('Customer linked successfully', { variant: 'success' });
      onLinked();
    } catch (err) {
      enqueueSnackbar(getFriendlyErrorMessage(err, 'Failed to link customer'), { variant: 'error' });
    } finally {
      setLinking(false);
    }
  }, [entityType, entityId, selectedId, syncDataToDris, enqueueSnackbar, onLinked]);

  const secondaryFor = (c: SageCustomerDetails): string | undefined =>
    [c.contactName, c.email, c.taxReference ? `Tax: ${c.taxReference}` : null]
      .filter(Boolean)
      .join(' · ') || undefined;

  return (
    <Dialog open={open} onClose={linking ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Link Sage Customer"
        icon={<LinkIcon color="primary" />}
        onClose={onClose}
        disabled={linking}
      />

      <DialogContent>
        <TextField
          fullWidth
          size="small"
          autoFocus
          label="Search Sage customers"
          placeholder="Type a name to search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          disabled={linking}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {searching && candidates.length === 0 ? (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : searchError ? (
            <Typography variant="body2" color="error" sx={{ p: 2 }}>
              Couldn&apos;t search Sage. Please try again.
            </Typography>
          ) : candidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {term.trim()
                ? 'No matches — refine your search'
                : 'Type a name to search Sage customers'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {candidates.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={selectedId === c.id}
                  onClick={() => setSelectedId(c.id)}
                  disabled={linking}
                >
                  <ListItemText primary={c.name} secondary={secondaryFor(c)} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={syncDataToDris}
              onChange={(e) => setSyncDataToDris(e.target.checked)}
              disabled={linking}
            />
          }
          label="Sync contact details from Sage to DRIS"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={linking}>
          Cancel
        </Button>
        <Button
          onClick={handleLink}
          variant="contained"
          disabled={linking || selectedId == null}
          startIcon={linking ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
        >
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SageLinkCustomerDialog;
